import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components';
import { Routes } from '@/constants';
import { AnalyticsService } from '@/services';
import {
  selectPlan,
  selectSessionUserId,
  useFetchUserStripeCustomerInfoQuery,
  useGenerateBillingPortalLinkMutation,
} from '@/store';
import { PlanSegment, Referrer } from '@/types';
import { SnackbarUtils } from '@/utils';

export const PlanActionButton = ({
  referrer,
  onClick,
  fallbackComponent = null,
  upgradeButtonLabel = 'Upgrade',
}: {
  referrer: Referrer;
  onClick?: () => void;
  fallbackComponent?: React.ReactNode;
  upgradeButtonLabel?: string;
}) => {
  const userId = useSelector(selectSessionUserId);
  const { data: plan } = useSelector(selectPlan);

  const { data: stripeData } = useFetchUserStripeCustomerInfoQuery();
  const {
    hasBillingPortalAccess: userHasBillingPortalLink,
    hasCurrentStripeSubscription: userHasStripeSubscription,
  } = stripeData || {};

  const [
    generateBillingPortalLink,
    { isLoading: isBillingPortalLinkGenerating },
  ] = useGenerateBillingPortalLinkMutation();

  const navigate = useNavigate();

  // Do not show button if user is not logged in or plan is B2B
  if (!userId || plan?.segment === PlanSegment.B2B) return fallbackComponent;

  const showBillingPortalLink =
    userHasBillingPortalLink && userHasStripeSubscription;

  const label = showBillingPortalLink
    ? 'Manage Subscription'
    : upgradeButtonLabel;

  const onViewBillingPortal = async () => {
    try {
      const { url } = await generateBillingPortalLink().unwrap();
      window.location.href = url;
    } catch {
      SnackbarUtils.error(
        'An error occurred while generating the billing portal link. Please try again later.',
      );
    }
  };

  const onViewPricing = () => {
    AnalyticsService.pageViewPricing({
      location: {
        ...referrer,
        button: label,
      },
    });
    navigate(Routes.PRICING);
  };

  const onButtonClick = () => {
    const action = showBillingPortalLink ? onViewBillingPortal : onViewPricing;
    action();
    onClick?.();
  };

  return (
    <Button
      variant="secondary"
      skinny
      label={label}
      fullWidth={false}
      isLoading={isBillingPortalLinkGenerating}
      onClick={onButtonClick}
    />
  );
};
